import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormGroup, FormBuilder, FormArray, FormControl, Validators } from '@angular/forms';
import { Farm } from 'src/app/shared/dtos/Farm';
import { FarmService } from '../../farm.service';
import { Router, ActivatedRoute } from '@angular/router';
import { FarmWithImages } from 'src/app/shared/dtos/FarmWithImages';
import { Image } from 'src/app/shared/dtos/Image';
import { NotificationService } from 'src/app/util/services/notification.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-create-farm',
  templateUrl: './create-farm.component.html',
  styleUrls: ['./create-farm.component.css']
})
export class CreateFarmComponent implements OnInit {

  modalRef: BsModalRef;

  myForm: FormGroup;
  myFormGeometry: FormGroup;
  isCreated = true;
  isEditable = true;

  isLinear = false;
  secondFormGroup: FormGroup;

  allFarms: Farm[];

  clicked = false;

  saving = false;
  isEdit: boolean;
  showStep1 = false;
  showStep2 = false;
  farmId: number;

  ubication = {};

  constructor(
    private fb: FormBuilder,
    private farmService: FarmService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.farmId = this.activatedRoute.snapshot.params['id'];
    this.isEdit = (this.farmId != undefined);
    this.createFormsInputs();
    if (this.isEdit){
      this.farmService.getFarmById(this.farmId).subscribe(farm => {
        this.myForm.patchValue(farm);
        this.myFormGeometry.patchValue(farm);
        if (farm.townHallDTO && farm.provinceDTO){
          this.myForm.get('country').setValue("es");
          this.myForm.get('province').setValue(farm.provinceDTO.idProvince);
          this.myForm.get('townHall').setValue(farm.townHallDTO.idTownHall);
        } else {
          this.myForm.get('country').setValue("pt");
          this.myForm.get('district').setValue(farm.districtDTO.idDistrict);
          this.myForm.get('municipality').setValue(farm.municipalityDTO.idMunicipality);
        }
        farm.exploitations.forEach(element => {
          (this.myForm.get('exploitations') as FormArray).push(new FormControl(element));
        });
        farm.objectives.forEach(element => {
          (this.myForm.get('objectives') as FormArray).push(new FormControl(element));
        });
        this.myForm.get('access').setValue(farm.accessRoad);
        this.showStep1 = true;
      });
    } else {
      this.showStep1 = true;
    }
    this.farmService.getFarms().subscribe(
      (farms) => {
        this.allFarms = farms;
      },
      () => {
        this.notificationService.showError(this.translate.instant('ERROR_GET_ALL_FARMS'), 5);
      }
    );
  }

  createFormsInputs() {
    this.myForm = this.fb.group({
          province          : new FormControl(),
          townHall          : new FormControl(),
          district          : new FormControl(),
          municipality      : new FormControl(),
          exploitations     : new FormArray([]),
          objectives        : new FormArray([]),
          access            : new FormControl(true, [Validators.required]),
          country           : new FormControl('es', [Validators.required]),
          area              : new FormControl('', [Validators.required]),
          information       : new FormControl('', [Validators.required]),
          listObjectFiles   : new FormControl()
      });
      this.myFormGeometry = this.fb.group({
        geometry: new FormControl('', [Validators.required])
      });
  }

  newImage(image: Image): FormGroup {
    return this.fb.group({
      format            : image.format,
      file              : image.file
    });
  }

  getImages(): FormArray {
    return this.myForm.get('listObjectFiles') as FormArray;
  }

  addImage(image: Image) {
    this.getImages().push(this.newImage(image));
  }

  addFarm(farm: FarmWithImages): void{
    if (this.myForm.get('country').value === 'es'){
      this.farmService.addFarmSpain(farm)
      .subscribe(
        () => {
          this.notificationService.showSuccess(this.translate.instant('SUCCESS_CREATE_FARM'), 5);
          this.router.navigate(['']);
        },
        () => {
          this.notificationService.showError(this.translate.instant('ERROR_CREATE_FARM'), 5);
        }
      );
    }else {
      this.farmService.addFarmPortugal(farm)
      .subscribe(
        () => {
          this.notificationService.showSuccess(this.translate.instant('SUCCESS_CREATE_FARM'), 5);
          this.router.navigate(['']);
        },
        () => {
          this.notificationService.showError(this.translate.instant('ERROR_CREATE_FARM'), 5);
        }
      );
    }
  }

  updateFarm(farm: Farm): void{
    if (this.myForm.get('country').value === 'es'){
      this.farmService.updateFarmSpain(farm)
      .subscribe(
        () => {
          this.notificationService.showSuccess(this.translate.instant('SUCCESS_UPDATE_FARM'), 5);
          this.router.navigate(['']);
        },
        () => {
          this.notificationService.showError(this.translate.instant('ERROR_UPDATE_FARM'), 5);
        }
      );
    }else {
      this.farmService.updateFarmPortugal(farm)
      .subscribe(
        () => {
          this.notificationService.showSuccess(this.translate.instant('SUCCESS_UPDATE_FARM'), 5);
          this.router.navigate(['']);
        },
        () => {
          this.notificationService.showError(this.translate.instant('ERROR_UPDATE_FARM'), 5);
        }
      );
    }
  }

  onSubmit() {
    this.clicked = true;
    this.saving = true;
    if (this.myForm.valid && this.myFormGeometry.valid) {
      const farmToSave: Farm = new Farm();
      const farmWithImage: FarmWithImages = new FarmWithImages();
      const farm = this.myForm.getRawValue();
      farm.geometry = this.myFormGeometry.getRawValue().geometry;
      farmToSave.geometry = farm.geometry;
      farmToSave.information = farm.information;
      farmToSave.accessRoad = farm.access;
      farmToSave.objectives = farm.objectives.map(x => (x.type || x));
      farmToSave.exploitations = farm.exploitations.map(x => (x.type || x));
      farmToSave.area = farm.area;
      farmToSave.available = true;
      if (farm.country === 'es'){
        farmToSave.townHall = farm.townHall;
        farmToSave.province = farm.province;
      }
      if (farm.country === 'pt'){
        farmToSave.district = farm.district;
        farmToSave.municipality = farm.municipality;
      }
      farmWithImage.object = farmToSave;
      farmWithImage.listObjectsFiles = farm.listObjectFiles;
      if (this.isEdit){
        farmToSave.id = this.farmId;
        this.updateFarm(farmToSave);
      } else {
        this.addFarm(farmWithImage);
      }
    } else {
      console.log("no valido");
    }
  }

}
